import React, { useEffect, useState } from 'react';
import {
  getSavedContactFilters,
  createSavedContactFilter,
  updateSavedContactFilter,
  deleteSavedContactFilter,
} from 'crm/_api/api';
import { useContactsFilters } from './ContactsFiltersContext';
import { savedFilterParams } from './model';

const SavedContactFilters = () => {
  const { intl, params, filters, replace, loading: loadingFields, error: fieldsError } = useContactsFilters();
  const [saved, setSaved] = useState([]);
  const [selected, setSelected] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const text = key => intl.formatMessage({ id: `crmContactFilters${key}` });
  const checked = response => {
    if (response.status >= 300) throw new Error();
    return response.data;
  };
  const load = async () => {
    setBusy(true);
    setError('');
    try {
      setSaved(checked(await getSavedContactFilters()));
    } catch (e) {
      setError(text('LoadError'));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const choose = id => {
    const item = saved.find(filter => String(filter.id) === id);
    setSelected(id);
    setTitle(item?.title || '');
    setConfirmDelete(false);
    setNotice('');
    if (item) replace({ ...item.query, page: 1 });
  };
  const save = async update => {
    if (!title.trim()) return;
    if (saved.some(item => item.title === title.trim() && (!update || String(item.id) !== selected))) {
      setError(text('DuplicateTitle'));
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    setConfirmDelete(false);
    try {
      const payload = { title: title.trim(), query: savedFilterParams(params, filters) };
      const item = checked(
        await (update ? updateSavedContactFilter(selected, payload) : createSavedContactFilter(payload))
      );
      setSaved(items => [...items.filter(filter => filter.id !== item.id), item]);
      setSelected(String(item.id));
      setTitle(item.title);
      setNotice(text('Saved'));
    } catch (e) {
      setError(text('SaveError'));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      checked(await deleteSavedContactFilter(selected));
      setSaved(items => items.filter(item => String(item.id) !== selected));
      choose('');
      setNotice(text('Deleted'));
    } catch (e) {
      setError(text('DeleteError'));
    } finally {
      setBusy(false);
    }
  };
  const disabled = busy || loadingFields || !!fieldsError;
  return (
    <div className="ContactsFilters__saved">
      <div className="ContactsFilters__buttons">
        <label>
          {text('SavedFilters')}
          <select value={selected} disabled={busy} onChange={event => choose(event.target.value)}>
            <option value="">{text('CurrentFilters')}</option>
            {saved.map(item => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          {text('Title')}
          <input value={title} maxLength={32} disabled={busy} onChange={event => setTitle(event.target.value)} />
        </label>
        <button type="button" disabled={disabled || !title.trim()} onClick={() => save(false)}>
          {text('SaveAsNew')}
        </button>
        {selected && (
          <>
            <button type="button" disabled={disabled || !title.trim()} onClick={() => save(true)}>
              {text('Update')}
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
              {text('Delete')}
            </button>
          </>
        )}
      </div>
      {confirmDelete && (
        <div className="ContactsFilters__buttons">
          <span>{text('ConfirmDelete')}</span>
          <button type="button" disabled={busy} onClick={remove}>
            {text('Delete')}
          </button>
          <button type="button" disabled={busy} onClick={() => setConfirmDelete(false)}>
            {text('Cancel')}
          </button>
        </div>
      )}
      {busy && <p role="status">{text('Loading')}</p>}
      {notice && <p role="status">{notice}</p>}
      {error && (
        <p role="alert" className="ContactsFilters__error">
          {error}{' '}
          <button type="button" disabled={busy} onClick={load}>
            {text('Reload')}
          </button>
        </p>
      )}
    </div>
  );
};

export default SavedContactFilters;
